import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';

export interface TourStep {
  /** CSS 選擇器，定位要高亮的真實元素 */
  target: string;
  title: string;
  text: string;
}

interface SpotlightTourProps {
  steps: TourStep[];
  open: boolean;
  /** 走完全部或跳過時呼叫（用來標記該頁已看過） */
  onFinish: () => void;
  labels: { next: string; prev: string; done: string; skip: string };
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PAD = 8; // spotlight 圈住元素時的內距
const FIND_TIMEOUT = 4000; // 等元素出現的上限（lazy-load / 資料未回）
const SCROLL_SETTLE = 250; // scrollIntoView 後等排版穩定再量測

export function SpotlightTour({ steps, open, onFinish, labels }: SpotlightTourProps) {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  const step = steps[index];
  const isLast = index === steps.length - 1;

  const goNext = useCallback(() => {
    setIndex((i) => {
      if (i < steps.length - 1) return i + 1;
      onFinish();
      return i;
    });
  }, [steps.length, onFinish]);

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // 開啟時重置到第一步
  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  // 定位 + 量測當前步驟的目標元素（含 robustness）
  useEffect(() => {
    if (!open || !step) return;

    setRect(null);
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    const startTime = Date.now();

    const begin = (el: Element) => {
      el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
      const measure = () => {
        const r = el.getBoundingClientRect();
        if (r.width > 0 || r.height > 0) {
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        }
      };
      const settleTimer = window.setTimeout(measure, SCROLL_SETTLE);
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      window.addEventListener('resize', measure);
      window.addEventListener('scroll', measure, true);
      cleanup = () => {
        window.clearTimeout(settleTimer);
        ro.disconnect();
        window.removeEventListener('resize', measure);
        window.removeEventListener('scroll', measure, true);
      };
    };

    const tryFind = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      const r = el?.getBoundingClientRect();
      if (el && r && (r.width > 0 || r.height > 0)) {
        begin(el);
        return;
      }
      if (Date.now() - startTime > FIND_TIMEOUT) {
        // 找不到（元素不存在/隱藏）→ 跳過這一步
        goNext();
        return;
      }
      window.setTimeout(tryFind, 100);
    };
    tryFind();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [open, index, step, goNext]);

  // ESC 跳過
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFinish();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onFinish]);

  if (!open || !step) return null;

  const zIndex = theme.zIndex.modal + 10;

  // spotlight 圓角矩形位置（圈住元素 + 內距）
  const spot = rect && {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  };

  // 文字卡擺位：spotlight 下方優先，靠下半部則改放上方；水平 clamp 進視窗
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const CARD_W = Math.min(360, vw - 32);
  let cardStyle: object = {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };
  if (spot) {
    const placeBelow = spot.top + spot.height < vh * 0.6;
    const left = Math.min(Math.max(spot.left, 16), vw - CARD_W - 16);
    cardStyle = placeBelow
      ? { top: spot.top + spot.height + 16, left }
      : { bottom: vh - spot.top + 16, left };
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex }}>
      {/* 點擊攔截層：擋住底下元素互動，避免導覽中誤觸 */}
      <Box sx={{ position: 'absolute', inset: 0, cursor: 'default' }} onClick={onFinish} />

      {/* spotlight：用超大 box-shadow 製造「除圈內外全暗」 */}
      {spot && (
        <Box
          sx={{
            position: 'absolute',
            top: spot.top,
            left: spot.left,
            width: spot.width,
            height: spot.height,
            borderRadius: '10px',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
            border: `2px solid ${theme.palette.primary.light}`,
            pointerEvents: 'none',
            transition: 'all 0.25s ease',
          }}
        />
      )}

      {/* 文字說明 + 控制（白字，沿用 email spotlight 風格） */}
      <Box
        sx={{
          position: 'absolute',
          width: CARD_W,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          color: 'common.white',
          bgcolor: 'rgba(0,0,0,0.88)',
          borderRadius: 2,
          p: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          ...cardStyle,
        }}
      >
        <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.5 }}>
          {step.title}
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.6, opacity: 0.9 }}>{step.text}</Typography>

        {/* 圓點 */}
        <Box sx={{ display: 'flex', gap: 0.75, py: 0.5 }}>
          {steps.map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: i === index ? 'common.white' : 'rgba(255,255,255,0.4)',
              }}
            />
          ))}
        </Box>

        {/* 按鈕列 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Button
            onClick={onFinish}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: 13,
              fontWeight: 600,
              minWidth: 0,
              px: 1,
            }}
          >
            {labels.skip}
          </Button>
          <Box sx={{ flex: 1 }} />
          {index > 0 && (
            <Button
              onClick={goPrev}
              sx={{
                color: 'common.white',
                fontSize: 13,
                fontWeight: 700,
                borderRadius: '33px',
                px: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              }}
            >
              {labels.prev}
            </Button>
          )}
          <Button
            onClick={goNext}
            variant="contained"
            sx={{ fontSize: 13, fontWeight: 700, borderRadius: '33px', px: 2.5 }}
          >
            {isLast ? labels.done : labels.next}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
